import { useDeferredValue, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { almacen, newOperationId, type BranchSerialUnit, type ModelStock, type SerialCountResult } from '../api';
import { CuadroDeBusqueda } from '../Buscable';
import { Boton, Cabecera, Cargando, Fallo, Filtro, Modal, Pastilla, Rotulo, Tarjeta, Vacio } from '../componentes';
import {
  FILTROS_DE_EQUIPOS,
  TEXTO_DE_ACCION,
  accionesDelEquipo,
  agregarAlConteo,
  segundoNumero,
  tonoDeEstado,
  type AccionDeEquipo,
} from '../equipos';
import { Escaner } from '../Escaner';
import { cantidad, dia, soles } from '../formato';
import { CONSULTA_DE_IMEI_OSIPTEL, ESTADOS_DEL_EQUIPO, type EstadoDelEquipo } from '../serie';

type Vista = 'equipos' | 'conteo' | 'modelos';

/**
 * Equipos por serie: cada celular o laptop del local, uno por fila (módulo
 * `serials`; docs/15 §2.41, segunda vuelta del 8 de octubre).
 *
 * Antes la serie solo se veía desde el insumo, un modelo a la vez, y moverla
 * —a la vitrina, al taller, al proveedor por garantía— no tenía pantalla: el
 * servidor lo sabía hacer y nadie podía pedírselo. Aquí está la lista de todos,
 * con dónde está cada uno, lo que se le puede hacer, el conteo por serie y el
 * mínimo de cada modelo sumando sus versiones.
 */
export function Series() {
  const [vista, setVista] = useState<Vista>('equipos');

  return (
    <>
      <Cabecera
        titulo="Equipos por serie"
        subtitulo="Cada equipo con su IMEI o su serie: dónde está, cuánto costó y qué le pasó"
        ayuda="La lista de todos los equipos del local, uno por fila. Desde aquí se mandan al proveedor por garantía, se reciben de vuelta, se pasan a la vitrina o al taller, y se cuenta el estante leyendo cada caja."
      />
      <div className="cuerpo">
        <div className="filtros">
          <Filtro texto="Equipos" activo={vista === 'equipos'} onClick={() => setVista('equipos')} />
          <Filtro texto="Contar por serie" activo={vista === 'conteo'} onClick={() => setVista('conteo')} />
          <Filtro texto="Mínimo por modelo" activo={vista === 'modelos'} onClick={() => setVista('modelos')} />
        </div>
        {/* Un equipo usado que entra o vuelve puede venir reportado como robado. */}
        <a className="enlace-sutil" href={CONSULTA_DE_IMEI_OSIPTEL} target="_blank" rel="noreferrer">
          Verificar un IMEI en OSIPTEL («Checa tu IMEI»)
        </a>
        {vista === 'equipos' ? <ListaDeEquipos /> : vista === 'conteo' ? <ContarPorSerie /> : <MinimoPorModelo />}
      </div>
    </>
  );
}

function ListaDeEquipos() {
  const [filtro, setFiltro] = useState(FILTROS_DE_EQUIPOS[0]!.id);
  const [texto, setTexto] = useState('');
  // Buscar mientras se teclea sin pedirle al servidor cada letra a destiempo.
  const buscado = useDeferredValue(texto);
  const estados = FILTROS_DE_EQUIPOS.find((uno) => uno.id === filtro)?.estados ?? [];

  const consulta = useQuery({
    queryKey: ['series-del-local', filtro, buscado],
    queryFn: () => almacen.branchSerialUnits({ statuses: estados, q: buscado }),
  });
  const [accion, setAccion] = useState<{ equipo: BranchSerialUnit; accion: AccionDeEquipo } | null>(null);
  const [editando, setEditando] = useState<BranchSerialUnit | null>(null);

  const equipos = consulta.data ?? [];

  return (
    <>
      <div className="filtros">
        <CuadroDeBusqueda
          valor={texto}
          onCambio={setTexto}
          placeholder="Buscar IMEI, serie, modelo o versión…"
          etiquetaAccesible="Buscar un equipo"
        />
        <span className="separador" />
        {FILTROS_DE_EQUIPOS.map((uno) => (
          <Filtro key={uno.id} texto={uno.texto} activo={filtro === uno.id} onClick={() => setFiltro(uno.id)} />
        ))}
      </div>

      {consulta.isError ? <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} /> : null}

      <div className="tabla-envoltura">
        {consulta.isLoading ? (
          <Cargando que="los equipos" />
        ) : equipos.length === 0 ? (
          <Vacio
            titulo="Ningún equipo aquí"
            mensaje={
              buscado.trim() === ''
                ? 'Los equipos aparecen al recibirlos por serie, desde Recepción.'
                : 'Ninguna serie, IMEI o modelo coincide con lo buscado en este filtro.'
            }
          />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Serie</th>
                <th>Modelo y versión</th>
                <th>Dónde está</th>
                <th className="numero">Costo</th>
                <th>Recibido</th>
                <th>Vendido o devuelto</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {equipos.map((equipo) => (
                <tr key={equipo.serialNumber}>
                  <td className="mono">
                    <div className="celda-nombre__texto">
                      <strong>{equipo.serialNumber}</strong>
                      <span>{equipo.secondarySerialNumber === null ? 'Sin IMEI 2' : `IMEI 2 ${equipo.secondarySerialNumber}`}</span>
                    </div>
                  </td>
                  <td>
                    <div className="celda-nombre__texto">
                      <strong>{equipo.model ?? equipo.supplyName}</strong>
                      <span>
                        {equipo.productName ?? equipo.supplyName} · {equipo.conditionLabel}
                      </span>
                    </div>
                  </td>
                  <td>
                    <Pastilla texto={equipo.statusLabel} tono={tonoDeEstado(equipo.status)} />
                  </td>
                  <td className="numero">{soles(equipo.unitCost)}</td>
                  <td className="apagado mono">{dia(equipo.receivedAt)}</td>
                  <td className="apagado mono">
                    {equipo.returnedAt !== null
                      ? `devuelto ${dia(equipo.returnedAt)}`
                      : equipo.soldAt !== null
                        ? dia(equipo.soldAt)
                        : '—'}
                  </td>
                  <td>
                    <div className="formulario__fila">
                      {accionesDelEquipo(equipo).map((una) => (
                        <Boton key={una} onClick={() => setAccion({ equipo, accion: una })}>
                          {TEXTO_DE_ACCION[una]}
                        </Boton>
                      ))}
                      <button type="button" className="accion-fila" onClick={() => setEditando(equipo)}>
                        IMEI 2
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {equipos.length === 500 ? (
        <p className="nota">Se enseñan los primeros 500: busca la serie o el modelo para ver el resto.</p>
      ) : null}

      {accion === null ? null : (
        <MoverEquipo equipo={accion.equipo} accion={accion.accion} onCerrar={() => setAccion(null)} />
      )}
      {editando === null ? null : <EditarSegundoNumero equipo={editando} onCerrar={() => setEditando(null)} />}
    </>
  );
}

/**
 * Confirmar el movimiento de un equipo. Con confirmación y no de un toque:
 * mandarlo al proveedor lo saca del libro, y recibirlo de vuelta lo pone a la
 * venta. El `operationId` nace al abrir: un doble clic no lo devuelve dos veces.
 */
function MoverEquipo({
  equipo,
  accion,
  onCerrar,
}: {
  equipo: BranchSerialUnit;
  accion: AccionDeEquipo;
  onCerrar: () => void;
}) {
  const clientes = useQueryClient();
  const [operationId] = useState(newOperationId);
  const [documento, setDocumento] = useState('');
  // Lo que vuelve del proveedor puede volver cambiado: uno nuevo por el dañado,
  // o reparado, que ya no es «nuevo».
  const [estado, setEstado] = useState<EstadoDelEquipo>(
    equipo.condition === 'new' ? 'refurbished' : (equipo.condition as EstadoDelEquipo),
  );

  const mover = useMutation({
    mutationFn: async () => {
      const papel = documento.trim() === '' ? undefined : documento.trim();
      switch (accion) {
        case 'a-vitrina':
          return almacen.setSerialUnitStatus(equipo.serialNumber, 'on_display');
        case 'al-taller':
          return almacen.setSerialUnitStatus(equipo.serialNumber, 'in_repair');
        case 'al-estante':
          return almacen.setSerialUnitStatus(equipo.serialNumber, 'in_stock');
        case 'garantia-desde-tienda':
          // Sale de su propio lote, que es el equipo: al costo que tuvo.
          await almacen.returnToSupplier({
            operationId,
            supplyId: equipo.supplyId,
            lotId: equipo.lotId,
            quantity: '1',
            document: papel ?? `Garantía ${equipo.serialNumber}`,
          });
          return;
        case 'garantia-devuelto':
          return almacen.sendReturnedSerialToSupplier(equipo.serialNumber);
        case 'volvio-del-proveedor':
          // La misma serie: el servidor la reconoce como la que estaba en el
          // proveedor y la devuelve a su lote, a su costo (migración 270).
          await almacen.receiveSerials({
            operationId,
            supplyId: equipo.supplyId,
            unitCost: equipo.unitCost,
            condition: estado,
            ...(papel === undefined ? {} : { document: papel }),
            units: [{ serialNumber: equipo.serialNumber }],
          });
          return;
      }
    },
    onSuccess: () => {
      void clientes.invalidateQueries();
      onCerrar();
    },
  });

  const explicacion: Record<AccionDeEquipo, string> = {
    'a-vitrina':
      'Sigue en el stock, pero la caja no lo ofrece al buscar el modelo: se vende solo si se escanea su serie.',
    'al-taller': 'Sigue en el stock, pero la caja no lo vende hasta que vuelva al estante.',
    'al-estante': 'Vuelve a la venta: la caja lo ofrece con los demás del modelo.',
    'garantia-desde-tienda': `Sale del stock como devolución al proveedor, a ${soles(equipo.unitCost)}. Queda «en el proveedor» y, cuando vuelva, se recibe desde aquí con su misma serie.`,
    'garantia-devuelto':
      'Lo devolvió un cliente y ya salió del stock al venderse: queda «en el proveedor» sin mover el libro. Cuando vuelva, se recibe desde aquí.',
    'volvio-del-proveedor': `Entra de nuevo al stock con su misma serie y a su costo de ${soles(equipo.unitCost)}, y la caja ya puede venderlo.`,
  };
  const pidePapel = accion === 'garantia-desde-tienda' || accion === 'volvio-del-proveedor';

  return (
    <Modal titulo={`${TEXTO_DE_ACCION[accion]}: ${equipo.serialNumber}`} onCerrar={onCerrar}>
      <div className="formulario">
        <p className="aviso-caja">{explicacion[accion]}</p>
        {pidePapel ? (
          <div className="campo">
            <Rotulo htmlFor="papel-garantia" ayuda="La guía, el número de caso del proveedor o la orden de servicio. Queda en el kardex.">
              Documento (opcional)
            </Rotulo>
            <input id="papel-garantia" maxLength={60} value={documento} onChange={(e) => setDocumento(e.target.value)} />
          </div>
        ) : null}
        {accion === 'volvio-del-proveedor' ? (
          <div className="campo">
            <Rotulo htmlFor="estado-vuelta" ayuda="Cómo vuelve: si el proveedor lo cambió por uno nuevo, «Nuevo»; si lo reparó, «Reacondicionado».">
              Estado al volver
            </Rotulo>
            <select id="estado-vuelta" value={estado} onChange={(e) => setEstado(e.target.value as EstadoDelEquipo)}>
              {ESTADOS_DEL_EQUIPO.map((uno) => (
                <option key={uno.valor} value={uno.valor}>
                  {uno.etiqueta}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {mover.isError ? <Fallo error={mover.error} /> : null}
        <div className="formulario__fila">
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton variante="primario" onClick={() => mover.mutate()} disabled={mover.isPending}>
            {mover.isPending ? 'Guardando…' : TEXTO_DE_ACCION[accion]}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/** El IMEI 2 o la serie de fábrica, para que la garantía lo encuentre por cualquiera. */
function EditarSegundoNumero({ equipo, onCerrar }: { equipo: BranchSerialUnit; onCerrar: () => void }) {
  const clientes = useQueryClient();
  const [texto, setTexto] = useState(equipo.secondarySerialNumber ?? '');
  const { valor, problema } = segundoNumero(equipo.serialNumber, texto);

  const guardar = useMutation({
    mutationFn: () => almacen.setSecondarySerial(equipo.serialNumber, valor),
    onSuccess: () => {
      void clientes.invalidateQueries({ queryKey: ['series-del-local'] });
      onCerrar();
    },
  });

  return (
    <Modal titulo={`IMEI 2 de ${equipo.serialNumber}`} onCerrar={onCerrar}>
      <div className="formulario">
        <div className="campo">
          <Rotulo
            htmlFor="imei-2"
            ayuda="El segundo IMEI de un celular de doble SIM, o la serie de fábrica de una laptop. El cliente vuelve con el que tiene a mano; vacío lo quita."
          >
            IMEI 2 o serie de fábrica
          </Rotulo>
          <input id="imei-2" maxLength={60} value={texto} onChange={(e) => setTexto(e.target.value)} />
        </div>
        {problema === null ? null : <p className="aviso-caja aviso-caja--peligro">{problema}</p>}
        {guardar.isError ? <Fallo error={guardar.error} /> : null}
        <div className="formulario__fila">
          <Boton onClick={onCerrar}>Cancelar</Boton>
          <Boton variante="primario" onClick={() => guardar.mutate()} disabled={guardar.isPending || problema !== null}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Contar por serie: se lee cada caja del estante, la vitrina y el taller, y se
 * compara con lo que el sistema cree que hay. **No mueve el libro**: dice
 * cuáles faltan y cuáles sobran. Arreglarlo es una merma, una recepción o un
 * traspaso de ese equipo; el conteo por cantidad sacaba el faltante de un
 * equipo cualquiera.
 */
function ContarPorSerie() {
  const insumos = useQuery({ queryKey: ['insumos'], queryFn: almacen.supplies });
  const modelos = (insumos.data ?? []).filter((insumo) => insumo.isSerialTracked === true);
  const [supplyId, setSupplyId] = useState('');
  const [leidas, setLeidas] = useState<string[]>([]);
  const [problema, setProblema] = useState<string | null>(null);

  const comparar = useMutation({
    mutationFn: () => almacen.countBySerial({ supplyId: supplyId === '' ? null : supplyId, serials: leidas }),
  });

  const leer = (texto: string) => {
    const resultado = agregarAlConteo(leidas, texto);
    setProblema(resultado.problema);
    setLeidas(resultado.series);
    comparar.reset();
  };

  return (
    <>
      <Tarjeta>
        <div className="formulario">
          <div className="campo">
            <Rotulo htmlFor="modelo-conteo" ayuda="Contar un solo modelo: lo que falta es de ese modelo, y una caja de otro que estaba en su estante sale como «de otro modelo».">
              Qué se cuenta
            </Rotulo>
            <select id="modelo-conteo" value={supplyId} onChange={(e) => setSupplyId(e.target.value)}>
              <option value="">Todos los equipos de la tienda</option>
              {modelos.map((insumo) => (
                <option key={insumo.id} value={insumo.id}>
                  {insumo.name}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <Rotulo htmlFor="lectura-conteo" ayuda="Escanea cada caja del estante, la vitrina y el taller. Vale el IMEI 1 o el 2; leer los dos de la misma caja la cuenta una vez.">
              Series leídas
            </Rotulo>
            <Escaner onCodigo={leer} etiqueta="Escanear la serie o el IMEI de cada caja" />
            {problema === null ? null : (
              <p className="aviso-caja aviso-caja--peligro" role="alert">
                {problema}
              </p>
            )}
          </div>
          <p className="nota nota--pegada">
            Leídas: <span className="mono">{leidas.length}</span>
            {leidas.length === 0 ? '' : ` · la última, ${leidas.at(-1)}`}
          </p>
          {comparar.isError ? <Fallo error={comparar.error} /> : null}
          <div className="formulario__fila">
            <Boton
              onClick={() => {
                setLeidas([]);
                setProblema(null);
                comparar.reset();
              }}
              disabled={leidas.length === 0}
            >
              Empezar de nuevo
            </Boton>
            <Boton variante="primario" onClick={() => comparar.mutate()} disabled={leidas.length === 0 || comparar.isPending}>
              {comparar.isPending ? 'Comparando…' : 'Comparar con el sistema'}
            </Boton>
          </div>
        </div>
      </Tarjeta>

      {comparar.data === undefined ? null : <ResultadoDelConteo resultado={comparar.data} />}
    </>
  );
}

function ResultadoDelConteo({ resultado }: { resultado: SerialCountResult }) {
  const enOrden = resultado.missing.length === 0 && resultado.extra.length === 0;
  return (
    <Tarjeta>
      <h2 className="tarjeta__titulo">
        {enOrden
          ? `Todo en orden: ${resultado.matched.length} equipos leídos y en la tienda`
          : `${resultado.matched.length} en orden · ${resultado.missing.length} faltan · ${resultado.extra.length} sobran`}
      </h2>
      {resultado.missing.length === 0 ? null : (
        <>
          <p className="nota">
            Faltan: el sistema los tiene en la tienda y nadie los leyó. Búscalos en la vitrina o el taller antes de darlos
            de baja desde Mermas.
          </p>
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>Serie</th>
                  <th>Dónde debería estar</th>
                </tr>
              </thead>
              <tbody>
                {resultado.missing.map((uno) => (
                  <tr key={uno.serialNumber}>
                    <td className="mono">{uno.serialNumber}</td>
                    <td>
                      <Pastilla texto={uno.statusLabel} tono="aviso" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {resultado.extra.length === 0 ? null : (
        <>
          <p className="nota">Sobran: se leyeron y el sistema no los tiene en esta tienda.</p>
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>Serie</th>
                  <th>Qué dice el sistema</th>
                </tr>
              </thead>
              <tbody>
                {resultado.extra.map((uno) => (
                  <tr key={uno.serialNumber}>
                    <td className="mono">{uno.serialNumber}</td>
                    <td>{uno.reasonLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Tarjeta>
  );
}

/**
 * El mínimo de cada modelo, sumando sus versiones. El de cada versión sigue
 * en su insumo; este dice «que no me falten iPhone 15», que es lo que piensa
 * el dueño: cinco versiones con una cada una no avisaban de nada.
 */
function MinimoPorModelo() {
  const consulta = useQuery({ queryKey: ['modelos'], queryFn: almacen.modelStock });
  const modelos = consulta.data ?? [];

  return (
    <>
      {consulta.isError ? <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} /> : null}
      <div className="tabla-envoltura">
        {consulta.isLoading ? (
          <Cargando que="los modelos" />
        ) : modelos.length === 0 ? (
          <Vacio
            titulo="Ningún modelo con versiones"
            mensaje="Un modelo aparece aquí cuando tiene más de una versión en la carta —por capacidad o color— y cada una descuenta su propio insumo."
          />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Modelo</th>
                <th className="numero">Versiones</th>
                <th className="numero">Quedan</th>
                <th>Mínimo del modelo</th>
              </tr>
            </thead>
            <tbody>
              {modelos.map((modelo) => (
                <FilaDeModelo key={modelo.familyId} modelo={modelo} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function FilaDeModelo({ modelo }: { modelo: ModelStock }) {
  const clientes = useQueryClient();
  const [texto, setTexto] = useState(
    // Sin los ceros de la derecha, y con punto: se vuelve a guardar tal cual.
    modelo.minimum === null ? '' : String(Number(modelo.minimum)),
  );
  const guardar = useMutation({
    mutationFn: () => almacen.setModelMinimum(modelo.familyId, texto.trim() === '' ? null : texto.trim()),
    onSuccess: () => void clientes.invalidateQueries(),
  });

  return (
    <tr>
      <td>
        <strong>{modelo.name}</strong>
        {modelo.belowMinimum ? (
          <>
            {' '}
            <Pastilla texto="bajo su mínimo" tono="aviso" />
          </>
        ) : null}
      </td>
      <td className="numero">{modelo.versions}</td>
      <td className="numero">
        {cantidad(modelo.quantity)} {modelo.unit ?? ''}
      </td>
      <td>
        <div className="formulario__fila">
          <input
            aria-label={`Mínimo de ${modelo.name}`}
            inputMode="decimal"
            placeholder="sin mínimo"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <Boton onClick={() => guardar.mutate()} disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
        {guardar.isError ? <Fallo error={guardar.error} /> : null}
      </td>
    </tr>
  );
}
